import { useState, useRef } from 'react';
import { isNative, isIOS } from '../capacitorInit';

interface Props {
  onSave: (blob: Blob, durationSec: number, label: string) => void;
  /** Fires true while recording or holding an unsaved preview, false once
   * idle again — lets a parent form block submit so a take isn't lost. */
  onBusyChange?: (busy: boolean) => void;
  idleLabel?: string;
}

/** Upload filename whose extension matches the recorded container — iOS
 * records audio/mp4, not webm, and the server keeps the extension. */
export function audioFileName(blob: Blob): string {
  if (blob.type.includes('mp4')) return 'recording.m4a';
  if (blob.type.includes('ogg')) return 'recording.ogg';
  return 'recording.webm';
}

/**
 * getUserMedia() failures are DOMExceptions whose raw `.message` is either
 * cryptic ("Permission denied") or a wall of spec text, and gives no path
 * to recover — once a user denies the mic prompt, the browser/OS never
 * re-prompts, so every future tap fails the exact same way with no hint
 * that Settings, not another tap, is the fix. This maps the handful of
 * getUserMedia error names to an actionable, platform-aware message.
 */
function describeMicError(err: unknown): string {
  const name = err instanceof DOMException ? err.name : undefined;
  const settingsHint = isNative
    ? isIOS
      ? 'Settings app → EventsCarousel → Microphone'
      : 'Settings → Apps → EventsCarousel → Permissions → Microphone'
    : "your browser's site settings (click the icon next to the address bar)";

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return `Microphone access is blocked. Enable it in ${settingsHint}, then try again.`;
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone was found on this device.';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'Your microphone is unavailable right now — it may be in use by another app.';
    case 'SecurityError':
      return 'Microphone access requires a secure connection.';
    default:
      return err instanceof Error ? err.message : 'Could not access the microphone.';
  }
}

export function AudioRecorder({ onSave, onBusyChange, idleLabel = 'Record Voice' }: Props) {
  const [state, setStateRaw] = useState<'idle' | 'recording' | 'preview'>('idle');
  function setState(next: 'idle' | 'recording' | 'preview') {
    setStateRaw(next);
    onBusyChange?.(next !== 'idle');
  }
  const [audioUrl, setAudioUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [label, setLabel] = useState('');
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startTime = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [recError, setRecError] = useState('');
  const previewBlob = useRef<Blob | null>(null);

  async function startRecording() {
    setRecError('');
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone access is not supported in this browser.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // iOS Safari only supports audio/mp4; Chrome/Firefox support audio/webm
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
                     : MediaRecorder.isTypeSupported('audio/mp4')  ? 'audio/mp4'
                     : '';

      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorder.current = mr;
      chunks.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunks.current.push(e.data); };
      mr.onstop = () => {
        const blobType = mimeType || mr.mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, { type: blobType });
        previewBlob.current = blob;
        setAudioUrl(URL.createObjectURL(blob));
        const dur = Math.round((Date.now() - startTime.current) / 1000);
        setDuration(dur);
        setState('preview');
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      startTime.current = Date.now();
      setElapsed(0);
      setState('recording');
      timerRef.current = setInterval(() => setElapsed(Math.round((Date.now() - startTime.current) / 1000)), 1000);
    } catch (err) {
      setRecError(describeMicError(err));
    }
  }

  function stopRecording() {
    if (timerRef.current) clearInterval(timerRef.current);
    mediaRecorder.current?.stop();
  }

  function discard() {
    setAudioUrl('');
    setLabel('');
    previewBlob.current = null;
    setState('idle');
  }

  function save() {
    if (previewBlob.current) onSave(previewBlob.current, duration, label.trim());
    discard();
  }

  if (state === 'idle') {
    return (
      <div className="space-y-1">
        <button
          type="button"
          onClick={startRecording}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
        >
          <span className="w-2 h-2 rounded-full bg-white"></span>
          {idleLabel}
        </button>
        {recError && <p className="text-xs text-red-600">{recError}</p>}
      </div>
    );
  }

  if (state === 'recording') {
    return (
      <div className="flex items-center gap-4">
        <span className="flex items-center gap-2 text-red-600 text-sm font-medium">
          <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
          Recording {elapsed}s
        </span>
        <button type="button" onClick={stopRecording} className="px-4 py-2 bg-stone-700 text-white rounded-lg text-sm hover:bg-stone-800">
          Stop
        </button>
      </div>
    );
  }

  return (
    <div className="border border-stone-200 rounded-lg p-3 space-y-2">
      <audio src={audioUrl} controls className="w-full h-8" />
      <input
        autoFocus
        type="text"
        value={label}
        onChange={e => setLabel(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
        placeholder="Label this recording (e.g. Grandma's story) — optional"
        className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <div className="flex gap-2">
        <button type="button" onClick={save} className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">
          Save Recording
        </button>
        <button type="button" onClick={discard} className="px-3 py-1 bg-stone-200 text-stone-700 rounded text-sm hover:bg-stone-300">
          Discard
        </button>
      </div>
    </div>
  );
}
